import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useMerchantContext } from './merchant-context'
import { useDataFields } from '../hooks/useDataField'
import { DataField } from '../dto/template'

interface IDataFieldContext {
  dataFields?: DataField[]
  isDataFieldsLoading?: boolean
  dataFieldsHash?: Map<number, DataField>
  addedDataFields?: Partial<DataField>[]
  addDataFieldToTemplate?: (
    dataField: Partial<DataField>,
    isAllData?: boolean
  ) => void
  resetAddedDataFields?: () => void
  refetchDataFields?: () => Promise<void>
}

const DataFieldContext = createContext<IDataFieldContext>({})

export const DataFieldProvider = ({ children }) => {
  const { selectedMerchant } = useMerchantContext()
  const { dataFieldsData, isDataFieldsLoading, refetchDataFields } =
    useDataFields(selectedMerchant?.id)
  const [dataFieldsHash, setDataFieldsHash] = useState<Map<number, DataField>>(
    new Map()
  )
  const [addedDataFields, setAddedDataFields] = useState<Partial<DataField>[]>( // DFs newly added to the template
    []
  )

  useEffect(() => {
    if (dataFieldsData) {
      const hash = new Map()
      dataFieldsData.forEach(dataField => {
        hash.set(dataField.id, dataField)
      })
      setDataFieldsHash(hash)
    } else {
      setDataFieldsHash(new Map())
    }
  }, [dataFieldsData])

  const addDataFieldToTemplate = (dataField: DataField, isAllData = false) => {
    const filteredArray = addedDataFields.filter(
      df =>
        df.dataset_path !== dataField.datasetPath &&
        df.dataset_path !== dataField.dataset_path
    )
    const value: Partial<DataField> = {
      name: dataField.name,
      dataset_path: dataField.datasetPath || dataField.dataset_path,
      data_type: dataField.dataType || dataField.data_type,
      required: dataField.required,
      schema: dataField.schema,
      merchant_evidence_id: dataField.merchant_evidence_id
    }

    if (isAllData) {
      value.id = dataField.id
      value.default_value = dataField.default_value
    }

    filteredArray.push(value)
    setAddedDataFields(filteredArray)
  }

  const resetAddedDataFields = () => setAddedDataFields([])

  const contextValue = useMemo(
    () => ({
      dataFields: dataFieldsData,
      isDataFieldsLoading,
      dataFieldsHash,
      addedDataFields,
      resetAddedDataFields,
      addDataFieldToTemplate,
      refetchDataFields
    }),
    [
      dataFieldsData,
      isDataFieldsLoading,
      dataFieldsHash,
      addedDataFields,
      resetAddedDataFields,
      addDataFieldToTemplate,
      refetchDataFields
    ]
  )

  return (
    <DataFieldContext.Provider value={contextValue}>
      {children}
    </DataFieldContext.Provider>
  )
}

export const useDataFieldContext = () => {
  return useContext(DataFieldContext)
}
